import React from "react";
import { ShieldCheck, Heart, TrendingUp, Stethoscope, Sparkles, Info } from "lucide-react";

export default function Protection() {
  return (
    <div className="space-y-8" data-testid="protection-container">
      <div className="relative rounded-3xl overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1758273241078-8eec353836be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHx3ZWxsbmVzcyUyMHRoZXJhcHklMjBzZXNzaW9ufGVufDB8fHx8MTc3ODAzMTgyNHww&ixlib=rb-4.1.0&q=85"
          alt="Wellness session"
          className="w-full h-72 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur mb-3">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.6} />
            <span className="text-xs">Ma protection</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl drop-shadow">Vous prendre soin de vous, c'est aussi cela.</h1>
          <p className="mt-2 text-white/90 max-w-2xl">Comprendre simplement à quoi vous avez droit en cas de coup dur, et préparer votre avenir, sereinement.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card icon={Stethoscope} title="Indemnités journalières (IJ)" testid="protection-arret">
          <p>En cas d'arrêt maladie, après <b>3 jours de carence</b>, vous pouvez percevoir des IJ calculées ainsi :</p>
          <p className="mt-2"><b>RAAM ÷ 730</b> où RAAM = CA moyen 3 ans × 66 % (abattement BNC).</p>
          <ul className="mt-3 space-y-1.5 text-xs">
            <li>• Minimum : <b>5,77 €/jour</b> (RAAM ≥ 4 806 €)</li>
            <li>• Maximum SSI : <b>65,84 €/jour</b> (PASS 2026)</li>
            <li>• Maximum CIPAV : jusqu'à <b>197,51 €/jour</b></li>
            <li>• Durée : 360 jours sur 3 ans (maladie)</li>
          </ul>
          <p className="mt-3 text-xs text-muted-soft">Exemple : 25 000 € de CA moyen → IJ ≈ <b>22,60 €/jour</b>.</p>
        </Card>

        <Card icon={ShieldCheck} title="Pension d'invalidité" testid="protection-invalidite">
          <p>En cas d'invalidité reconnue, votre pension dépend du taux d'invalidité et du PASS 2026 (48 060 €) :</p>
          <ul className="mt-3 space-y-1.5 text-xs">
            <li>• Invalidité partielle : ~30 % du revenu moyen plafonné</li>
            <li>• Invalidité totale : <b>50 % du revenu moyen plafonné au PASS</b></li>
            <li>• Pension minimum : ~310 €/mois</li>
          </ul>
          <p className="mt-3 text-xs text-muted-soft">Une prévoyance complémentaire est <b>fortement recommandée</b> pour combler la différence avec votre revenu réel.</p>
        </Card>

        <Card icon={TrendingUp} title="Retraite estimée" testid="protection-retraite">
          <p>En micro-BNC SSI, votre retraite (base + complémentaire) reste <b>très modeste</b> :</p>
          <ul className="mt-3 space-y-1.5 text-xs">
            <li>• CA 20 000 €/an pendant 25 ans → ≈ <b>500–700 €/mois</b></li>
            <li>• CA 35 000 €/an pendant 25 ans → ≈ <b>900–1 200 €/mois</b></li>
            <li>• CA 50 000 €/an pendant 25 ans → ≈ <b>1 200–1 500 €/mois</b></li>
          </ul>
          <p className="mt-3 text-xs text-muted-soft">Un PER permet souvent de <b>doubler</b> ce montant — et les versements sont déductibles de votre impôt.</p>
        </Card>
      </div>

      <div className="card-soft p-7 lg:p-10" style={{ background: "linear-gradient(135deg, var(--surface) 0%, var(--sage-light) 100%)" }} data-testid="ssi-cipav-card">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-sage flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-display text-xl mb-1">SSI ou Cipav ? Comment savoir.</div>
            <p className="text-sm leading-relaxed">
              Le taux de cotisations dépend de votre activité et de votre régime. En 2026 :
              <b> 25,6 %</b> pour les professions libérales non réglementées hors Cipav
              et <b>23,2 %</b> pour les professions relevant de la Cipav.
              Depuis 2018, les nouvelles activités libérales non réglementées (sophrologue, naturopathe,
              hypnothérapeute, énergéticien, coach bien-être, masseur, praticien Reiki/EFT…) relèvent
              généralement de la SSI. Les professions libérales réglementées historiques
              (ostéopathe DO, psychomotricien, etc.) relèvent de la Cipav.
              Si vous avez créé votre activité avant 2018, vous pouvez être encore à la Cipav.
              <b> Vérifiez impérativement votre situation</b> sur votre attestation URSSAF
              ou auprès de votre expert-comptable — nous n'appliquons aucun taux par défaut à votre place.
            </p>
          </div>
        </div>
      </div>

      <div className="card-soft p-7 lg:p-10" data-testid="recommendations-card">
        <div className="font-display text-2xl mb-2">Trois pistes simples pour mieux vous protéger</div>
        <p className="text-muted-soft mb-6">Pas de jargon, pas de stress — juste de quoi avancer doucement.</p>
        <div className="grid lg:grid-cols-3 gap-5">
          <Reco
            n="1"
            title="Plan d'Épargne Retraite (PER)"
            text="Versements déductibles de l'impôt — un capital ou une rente à la retraite. À partir de 50 €/mois."
          />
          <Reco
            n="2"
            title="Mutuelle santé"
            text="Une mutuelle adaptée aux indépendants couvre soin, optique, dentaire — souvent dès 30 €/mois."
          />
          <Reco
            n="3"
            title="Prévoyance complémentaire"
            text="Compense la perte de revenus en cas d'arrêt long. Un filet de sécurité essentiel quand les IJ ne suffisent pas."
          />
        </div>
      </div>

      <div className="card-soft p-7 lg:p-10" data-testid="contact-card" style={{ background: "linear-gradient(135deg, var(--surface) 0%, var(--sage-light) 100%)" }}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-sage flex items-center justify-center flex-shrink-0">
            <Heart className="w-5 h-5 text-white" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-display text-xl mb-1">Envie d'en parler avec un humain ?</div>
            <p className="text-muted-soft text-sm leading-relaxed mb-4">
              Capsule Patrimoine accompagne les indépendants du bien-être pour clarifier leur situation, sans pression.
              Un échange de 30 min, sans engagement.
            </p>
            <a href="mailto:contact@capsulepatrimoine.com?subject=Rendez-vous%20Capsule%20Patrimoine" className="btn-sage text-sm" data-testid="contact-email-btn">
              contact@capsulepatrimoine.com
            </a>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-soft text-center px-6">
        Estimations indicatives basées sur les barèmes URSSAF / CNAVPL / CIPAV 2026. Ne remplacent pas un conseil personnalisé.
      </p>
    </div>
  );
}

function Card({ icon: Icon, title, children, testid }) {
  return (
    <div className="card-soft p-7" data-testid={testid}>
      <div className="w-11 h-11 rounded-xl bg-sage-light flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-sage" strokeWidth={1.5} />
      </div>
      <div className="font-display text-lg mb-2">{title}</div>
      <div className="text-sm text-muted-soft leading-relaxed">{children}</div>
    </div>
  );
}

function Reco({ n, title, text }) {
  return (
    <div className="rounded-2xl p-6 border" style={{ borderColor: "var(--border)" }}>
      <div className="w-9 h-9 rounded-full bg-beige flex items-center justify-center font-display mb-3" style={{ color: "#3A352D" }}>{n}</div>
      <div className="font-display text-base mb-1">{title}</div>
      <div className="text-sm text-muted-soft leading-relaxed">{text}</div>
    </div>
  );
}
